import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { OutletHistoryResponse } from '../../../../../services/transformerAmqpService';
import { parseUtcTimestamp, parseUtcTimestampMs } from '../../../../../utils/timeUtils';
import { fmtOpt } from '../../../../../utils/numberFormat';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface HistoryLineChartProps {
  historyData: OutletHistoryResponse | null;
  category: string;
  unit: string;
  startDate: Date;
  endDate: Date;
}

// Phase colors
const phaseColors = {
  L1: { border: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)' },   // Red
  L2: { border: 'rgb(234, 179, 8)', bg: 'rgba(234, 179, 8, 0.1)' },    // Yellow
  L3: { border: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.1)' },  // Blue
  N: { border: 'rgb(107, 114, 128)', bg: 'rgba(107, 114, 128, 0.1)' }, // Gray
  Ges: { border: 'rgb(139, 92, 246)', bg: 'rgba(139, 92, 246, 0.1)' }, // Purple
};

// Map measurement type/IEC name to phase
const getPhaseFromSeries = (measurementType: string | null, iecName: string | null): string | null => {
  const type = (measurementType ?? '').toLowerCase();
  const iec = (iecName ?? '').toLowerCase();

  if (type.includes('phase_a') || iec.includes('.phsa')) return 'L1';
  if (type.includes('phase_b') || iec.includes('.phsb')) return 'L2';
  if (type.includes('phase_c') || iec.includes('.phsc')) return 'L3';
  if (type.includes('neutral') || iec.includes('.neut')) return 'N';
  if (type.includes('total') || iec.includes('tot')) return 'Ges';

  return null;
};

// Map measurement type/IEC name to category
const getCategoryFromSeries = (measurementType: string | null, iecName: string | null): string => {
  const type = (measurementType ?? '').toLowerCase();
  const iec = (iecName ?? '').toLowerCase();

  const iecParts = iec.split('.');
  if (iecParts.length >= 2) {
    const iecCat = iecParts[1].toLowerCase();
    if (iecCat === 'va' || iecCat === 'totva') return 'apparent_power';
    if (iecCat === 'var' || iecCat === 'totvar') return 'reactive_power';
    if (iecCat === 'w' || iecCat === 'totw') return 'active_power';
    if (iecCat === 'a') return 'current';
    if (iecCat === 'phv') return 'voltage';
  }

  if (type.includes('apparent')) return 'apparent_power';
  if (type.includes('reactive')) return 'reactive_power';
  if (type.includes('power') || type.includes('active')) return 'active_power';
  if (type.includes('current')) return 'current';
  if (type.includes('voltage')) return 'voltage';

  return 'other';
};

export const HistoryLineChart: React.FC<HistoryLineChartProps> = ({
  historyData,
  category,
  unit,
  startDate,
  endDate,
}) => {
  const chartData = useMemo(() => {
    if (!historyData || historyData.series.length === 0) return null;

    // Filter series for this category
    const relevantSeries = historyData.series.filter(s => {
      return getCategoryFromSeries(s.measurementType, s.iecName) === category;
    });

    if (relevantSeries.length === 0) return null;

    const rangeStart = startDate.getTime();
    const rangeEnd = endDate.getTime();

    // Group series by phase first to avoid duplicates
    const phaseDataMap = new Map<string, Map<string, number>>();
    const timestampSet = new Set<string>();

    relevantSeries.forEach(series => {
      const phase = getPhaseFromSeries(series.measurementType, series.iecName) || 'Ges';

      if (!phaseDataMap.has(phase)) {
        phaseDataMap.set(phase, new Map());
      }
      const dataMap = phaseDataMap.get(phase)!;

      series.dataPoints.forEach(dp => {
        // Filter by date range
        const t = parseUtcTimestampMs(dp.hourTimestamp);
        if (t < rangeStart || t > rangeEnd) return;
        timestampSet.add(dp.hourTimestamp);
        // Use average of min/max for the line
        // If we already have data for this timestamp, keep the existing value
        if (!dataMap.has(dp.hourTimestamp)) {
          dataMap.set(dp.hourTimestamp, (dp.minValue + dp.maxValue) / 2);
        }
      });
    });

    const timestamps = Array.from(timestampSet).sort((a, b) =>
      parseUtcTimestampMs(a) - parseUtcTimestampMs(b)
    );

    if (timestamps.length === 0) return null;

    // Create one dataset per phase
    const phaseOrder = ['L1', 'L2', 'L3', 'N', 'Ges'];
    const datasets = phaseOrder
      .filter(phase => phaseDataMap.has(phase))
      .map(phase => {
        const dataMap = phaseDataMap.get(phase)!;
        const colors = phaseColors[phase as keyof typeof phaseColors] || phaseColors.Ges;

        return {
          label: phase,
          data: timestamps.map(ts => dataMap.get(ts) ?? null),
          borderColor: colors.border,
          backgroundColor: colors.bg,
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 2,
        };
      });

    return {
      labels: timestamps.map(ts => {
        const d = parseUtcTimestamp(ts);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
      }),
      datasets,
    };
  }, [historyData, category, startDate, endDate]);

  if (!chartData) {
    return null;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 15,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'line'>) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${fmtOpt(value, 2)} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 8,
          font: { size: 10 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: { size: 10 },
          callback: (value: string | number) => `${value} ${unit}`,
        },
      },
    },
  };

  return (
    <div className="h-48 mb-4">
      <Line data={chartData} options={options} />
    </div>
  );
};

export default HistoryLineChart;
